import { trackAmplitudeEvent } from '../../analytics/CustomEvents'
import { t } from '../../utils/search/translations'
import { facetValueRetrievalLimit } from '../../utils/search/facetLimits'

// The refinement-list facets from filterWidgets.js. Toggle filters (Delivery,
// Professional details, Sellers, Trade) are excluded: each of their rows is an
// independent AND filter, so "select all" would strangle results.
function selectAllAttributes () {
  return [
    'designed_by_name',
    'year_of_manufacture',
    'brand_name',
    'age_group_name',
    'vibe_names',
    'color_name',
    'main_material_name',
    'country_from_address',
    'address_city',
    'number_of_doors',
    'number_of_drawers',
    'number_of_shelves',
    'number_of_seats',
    'feature_names',
    'seating_shape_names',
    'door_type_name',
    'shape_name',
    'orientation_name',
    'art_size_name',
    'art_subject_names',
    'artistic_movement_names',
    'set_of'
  ]
}

// Mirrors the transformItems filtering the widgets apply, so we never refine a
// value the list doesn't show
export function selectableFacetValues (attribute, facetValues) {
  const values = facetValues || []

  if (attribute === 'number_of_seats') {
    // The seats list caps at "4+ seater" (value 4); 5+ raw values are hidden
    return values.filter(value => parseFloat(value.name) <= 4)
  }

  if (['year_of_manufacture', 'brand_name'].includes(attribute)) {
    // These widgets strip "Unknown" via removeUnknowns
    return values.filter(value => value.name.toLowerCase() !== 'unknown')
  }

  return values
}

// Null means the facet gets no row
export function selectAllRowState (attribute, facetValues, refinedCount) {
  const values = facetValues || []

  // Truncated facets (Brand, Designer, ...) never offer "Select all" — we can't see
  // the full value list, and hundreds of URL-serialised refinements break reload
  if (values.length >= facetValueRetrievalLimit()) {
    if (refinedCount === 0) return null
    return { action: 'deselect' }
  }

  const selectable = selectableFacetValues(attribute, values)
  if (selectable.length < 2) return null
  if (selectable.every(value => value.isRefined)) return { action: 'deselect' }

  return { action: 'select' }
}

function refinedValueCount (state, attribute) {
  const refinements = state && state.disjunctiveFacetsRefinements
  if (!refinements || !refinements[attribute]) return 0

  return refinements[attribute].length
}

// After "Deselect all" the facet has no refinements left, so the panel's collapsed()
// re-collapses it on the next render — under the user's cursor. The click handler
// leaves a one-shot note on the panel; honour it here, after the panels have rendered.
function keepPanelOpenAfterBulkClear (panel) {
  if (panel.dataset.keepOpenAfterBulkClear !== 'true') return

  delete panel.dataset.keepOpenAfterBulkClear
  panel.classList.remove('ais-Panel--collapsed')
}

// .ais-Panel-body's child is appended imperatively by the Panel component (via a
// ref), not rendered by Preact — so a row prepended here survives re-renders
function ensureRow (panelBody, attribute) {
  const existing = panelBody.querySelector('.vjs-select-all-toggle')
  if (existing) return existing

  const row = document.createElement('label')
  row.className = 'ais-RefinementList-label'
  row.innerHTML = `
    <input type="checkbox" class="ais-RefinementList-checkbox vjs-select-all-toggle" data-attribute="${attribute}">
    <span class="ais-RefinementList-labelText font-bold"></span>
  `
  panelBody.prepend(row)

  return row.querySelector('.vjs-select-all-toggle')
}

function removeRow (panelBody) {
  const toggle = panelBody.querySelector('.vjs-select-all-toggle')
  if (toggle) toggle.closest('label').remove()
}

function syncRow (container, results, state, attribute) {
  const panel = container.querySelector(`.ais-Panel.facet-name-${attribute}`)
  if (!panel) return

  keepPanelOpenAfterBulkClear(panel)

  const panelBody = panel.querySelector('.ais-Panel-body')
  if (!panelBody) return

  const facetValues = results.getFacetValues(attribute)
  const rowState = selectAllRowState(attribute, facetValues, refinedValueCount(state, attribute))

  if (!rowState) {
    removeRow(panelBody)
    return
  }

  const toggle = ensureRow(panelBody, attribute)
  toggle.dataset.action = rowState.action
  toggle.checked = rowState.action === 'deselect'
  toggle.closest('label').querySelector('.ais-RefinementList-labelText')
    .textContent = t(rowState.action === 'deselect' ? 'deselect_all' : 'select_all')
}

// Runs on every instantsearch render, after the panel widgets have rendered
export function syncSelectAllRows (renderOptions) {
  const container = document.getElementById('vjs-filters-container')
  if (!container || !renderOptions.results) return

  selectAllAttributes().forEach(attribute => {
    syncRow(container, renderOptions.results, renderOptions.state, attribute)
  })
}

function selectAllValues (helper, attribute) {
  selectableFacetValues(attribute, helper.lastResults.getFacetValues(attribute))
    .filter(value => !value.isRefined)
    .forEach(value => helper.addDisjunctiveFacetRefinement(attribute, value.name))

  trackAmplitudeEvent('PLP Filter Select All Clicked', { facet_attribute: attribute })
}

function deselectAllValues (helper, toggle, attribute) {
  const panel = toggle.closest('.ais-Panel')
  if (panel) panel.dataset.keepOpenAfterBulkClear = 'true'

  helper.removeDisjunctiveFacetRefinement(attribute)

  trackAmplitudeEvent('PLP Filter Deselect All Clicked', { facet_attribute: attribute })
}

export function addSelectAllClickListener (search) {
  const container = document.getElementById('vjs-filters-container')
  if (!container || container.dataset.selectAllListenerAdded) return

  container.dataset.selectAllListenerAdded = 'true'

  container.addEventListener('click', event => {
    const toggle = event.target
    if (!toggle.classList || !toggle.classList.contains('vjs-select-all-toggle')) return

    const attribute = toggle.dataset.attribute
    if (toggle.dataset.action === 'deselect') {
      deselectAllValues(search.helper, toggle, attribute)
    } else {
      selectAllValues(search.helper, attribute)
    }

    search.helper.search()
  })
}
